/**
 * PreviewSurfaces — the canonical mini-renderings of each app surface a
 * brand change can touch. Used by BOTH the docked preview rail and the
 * floating PIP window. Keep in sync with the real surfaces:
 *   • LoginPreview  ↔ frontend/src/modules/auth/pages/LoginPage.tsx
 *   • HomePreview   ↔ Home / dashboard agent tiles
 *   • ChatPreview   ↔ Conversation header + message bubbles
 *   • SidebarPreview ↔ frontend/src/components/layout/Sidebar.tsx
 *   • ModalPreview  ↔ Generic Ant Design modal chrome
 *
 * Each surface receives the merged `BrandConfig` directly and reads colors /
 * fonts / strings from it (no CSS-var indirection) so the preview is faithful
 * even before applyBrandCSS() runs. The only place CSS vars are used is for
 * the chrome around the surface (background, border) — those flow from the
 * <PreviewScope> wrapper.
 */

import { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import type { BrandConfig } from '@/brand/types';
import { BrandScope } from '@/brand';
import { useLanguage } from '@/hooks/useLanguage';
import { LoginBrandingPanel } from '@modules/auth/components/LoginBrandingPanel';
/* Brij icon — used by the "Powered by Brij" attribution rendered at the
   bottom of the Sidebar preview. The real sidebar imports the same asset;
   we mirror it here so the editor preview matches what tenants see. */
import brijIconBlack from '@/brand-assets/brij/icon-black.svg';
import {
  HomePreview, AgentsPreview, ChatPreview, ConversationPreview,
  LibraryPreview, EditAgentPreview,
} from './RealPagePreviews';

export type PreviewSurface =
  | 'login' | 'home' | 'agents' | 'chat' | 'conversation'
  | 'library' | 'editAgent' | 'sidebar' | 'modal';

export const PREVIEW_SURFACE_LABEL_KEY: Record<PreviewSurface, string> = {
  login:        'designSystem.surface.login',
  home:         'designSystem.surface.home',
  chat:         'designSystem.surface.chat',
  conversation: 'designSystem.surface.conversation',
  agents:       'designSystem.surface.agents',
  library:      'designSystem.surface.library',
  editAgent:    'designSystem.surface.editAgent',
  sidebar:      'designSystem.surface.sidebar',
  modal:        'designSystem.surface.modal',
};

/** One-sentence explainer per surface — used in the preview header tooltip. */
export const PREVIEW_SURFACE_HINT_KEY: Record<PreviewSurface, string> = {
  login:        'designSystem.surfaceHint.login',
  home:         'designSystem.surfaceHint.home',
  chat:         'designSystem.surfaceHint.chat',
  conversation: 'designSystem.surfaceHint.conversation',
  agents:       'designSystem.surfaceHint.agents',
  library:      'designSystem.surfaceHint.library',
  editAgent:    'designSystem.surfaceHint.editAgent',
  sidebar:      'designSystem.surfaceHint.sidebar',
  modal:        'designSystem.surfaceHint.modal',
};

/* ─── Scope wrapper — applies the merged brand to a subtree via CSS vars ─── */

export function PreviewScope({
  merged,
  children,
}: {
  merged: BrandConfig;
  children: React.ReactNode;
}) {
  const style = {
    '--primary': merged.colors.primary,
    '--primary-hover': merged.colors.primaryHover,
    '--secondary': merged.colors.secondary,
    '--accent': merged.colors.accent,
    '--gradient-primary': merged.colors.gradientPrimary,
    '--bg-primary':
      merged.lightMode?.bgPrimary || merged.darkMode?.bgPrimary || 'var(--bg-primary)',
    '--bg-secondary':
      merged.lightMode?.bgSecondary || merged.darkMode?.bgSecondary || 'var(--bg-secondary)',
    // Font-family is deliberately NOT set here. The editor injects a scoped
    // stylesheet carrying the exact stacks apply-brand.ts uses globally; an
    // inline font here would win over it and render a different fallback
    // chain than the real page — the preview would lie about the font.
    '--font-heading': merged.fonts?.heading ?? '',
    '--font-body': merged.fonts?.body ?? '',
    '--font-mono': merged.fonts?.mono ?? '',
  } as React.CSSProperties;
  // CSS variables paint the colours, but a real component mounted inside a
  // preview asks `useBrand()` for its LOGO, its name and its assets — and
  // without a scope it would get the signed-in admin's brand back and show
  // the wrong logo on every surface. BrandScope answers that question with
  // the tenant being edited.
  return (
    <BrandScope brand={merged}>
      <div className="wl-preview-scope" style={style}>{children}</div>
    </BrandScope>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */

/* ─── Login ────────────────────────────────────────────────────────────── */

/**
 * PreviewViewport — lays a surface out at a REAL viewport size, then CSS-scales
 * the whole thing down to fit the preview rail.
 *
 * This is what lets a preview render the actual page component instead of a
 * miniature rebuild of it. The page gets the width it was designed for, so its
 * own media queries, flex ratios and type scale behave exactly as they do for a
 * user; `transform: scale()` then shrinks the finished pixels. Rebuilding a
 * surface small always drifts — this cannot.
 */
export function PreviewViewport({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, (el.clientWidth || width) / width));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div
      ref={ref}
      className="wl-preview-viewport"
      // The scaled page still occupies its ORIGINAL box for layout purposes,
      // so the wrapper has to claim the scaled height itself or the rail keeps
      // a full-size hole under the preview.
      style={{ height: height * scale }}
    >
      <div
        className="wl-preview-viewport-page"
        style={{ width, height, transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The login surface is the REAL login page, not a lookalike.
 *
 * It renders the same `LoginBrandingPanel` the login route renders, and mirrors
 * the form panel's markup class for class, inside a `PreviewViewport`. That is
 * what makes the preview trustworthy: whatever the login page does, this does.
 * The previous hand-built approximation had its own class names, its own tick
 * icon and no animated brand frame, so it drifted from the real page silently.
 */
export function LoginPreview({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  return (
    <PreviewViewport width={1280} height={800}>
      <div className="login-page">
        <LoginBrandingPanel brand={merged} logo={merged.logo} linkLogo={false} />

        {/* Form panel — the real page's markup with the controls inert. */}
        <div className="login-form-panel">
          <div className="form-container">
            <div className="form-header">
              <h1 className="form-title">{t('login.welcomeBack')}</h1>
            </div>

            <form className="login-form" onSubmit={e => e.preventDefault()}>
              <div className="form-group">
                <label className="form-label">{t('login.email')}</label>
                <div className="input-row">
                  <div className="input-wrapper">
                    <input className="form-input" placeholder="name@company.com" readOnly />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('login.password')}</label>
                <div className="input-row">
                  <div className="input-wrapper">
                    <input
                      type="password"
                      className="form-input has-toggle"
                      placeholder={t('login.passwordPlaceholder')}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-container">
                  <input type="checkbox" readOnly />
                  <span className="checkmark" />
                  <span className="checkbox-text">{t('login.rememberMe')}</span>
                </label>
                <a className="forgot-link" href="#" onClick={e => e.preventDefault()}>
                  {t('login.forgotPassword')}
                </a>
              </div>

              <button type="button" className="login-btn"><span>{t('login.signIn')}</span></button>
            </form>

            <div className="form-footer">
              <p className="footer-text">{t('login.securityNote')}</p>
              <div className="legal-links">
                <a href="#" onClick={e => e.preventDefault()}>{t('legal.nav.privacy')}</a>
                <span className="legal-separator" aria-hidden="true">·</span>
                <a href="#" onClick={e => e.preventDefault()}>{t('legal.nav.terms')}</a>
              </div>
            </div>
          </div>
        </div>

        {/* Powered-by pill — the real page's, minus the outbound link. */}
        <div className="powered-by-brij" aria-label="Powered by Brij">
          <span className="powered-by-text">
            Powered by <span className="powered-by-brand">Brij</span>
          </span>
          <img src={brijIconBlack} alt="" className="powered-by-icon" aria-hidden="true" />
        </div>
      </div>
    </PreviewViewport>
  );
}



/* ─── Agents (dedicated page) ──────────────────────────────────────────── */



/* ─── Sidebar ──────────────────────────────────────────────────────────── */

export function SidebarPreview({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  // Mirror the real Sidebar layout: header w/ icon + brand name, nav items,
  // active item tinted with brand primary at 10% alpha.
  const navItems = [
    { label: t('designSystem.home'),   active: true  },
    { label: t('nav.agents'), active: false },
    { label: t('skills.pageTitle'), active: false },
    { label: t('nav.admin'),  active: false },
  ];
  const activeBg = `${merged.colors.primary}1A`; // 10% alpha hex
  return (
    <div className="wl-preview-sidebar" style={{ background: 'var(--bg-secondary)' }}>
      <div className="wl-preview-sidebar-header">
        {merged.icon && <img src={merged.icon} alt="" />}
        <span style={{ fontFamily: merged.fonts?.heading, fontWeight: 600 }} dir="auto">{merged.name}</span>
      </div>
      <nav className="wl-preview-sidebar-nav">
        {navItems.map(item => (
          <div
            key={item.label}
            className={`wl-preview-sidebar-item ${item.active ? 'wl-preview-sidebar-item--active' : ''}`}
            style={item.active ? { background: activeBg, color: merged.colors.primary } : undefined}
          >
            <span className="wl-preview-sidebar-bullet" style={{ background: item.active ? merged.colors.primary : 'transparent' }} />
            {item.label}
          </div>
        ))}
      </nav>
      <div className="wl-preview-sidebar-footer">
        <div className="wl-preview-sidebar-avatar">A</div>
        <div className="wl-preview-sidebar-userlines">
          <div style={{ fontWeight: 600 }}>{t('designSystem.sampleUserName')}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{merged.companyName}</div>
        </div>
      </div>
      {/* Powered by Brij — quiet attribution; same markup as the real
          sidebar (see `Sidebar.tsx .rail-powered-by`). NOT white-labeled
          — every tenant sees the Brij mark here. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 12px 4px',
        fontSize: 11, letterSpacing: '0.2px',
        color: 'var(--text-tertiary, #94a3b8)',
        opacity: 0.7,
      }}>
        <span>
          {t('designSystem.poweredByPrefix')}<span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Brij</span>
        </span>
        <img src={brijIconBlack} alt="" aria-hidden style={{
          height: 12, width: 'auto', display: 'block', flexShrink: 0,
        }} />
      </div>
    </div>
  );
}

/* ─── Modal ────────────────────────────────────────────────────────────── */

export function ModalPreview({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  return (
    <div className="wl-preview-modal-stage">
      <div className="wl-preview-modal" style={{ background: 'var(--bg-elevated, var(--bg-secondary))' }}>
        <header className="wl-preview-modal-header">
          <span style={{ fontFamily: merged.fonts?.heading, fontWeight: 600 }}>{t('designSystem.editProfile')}</span>
          <span className="wl-preview-modal-close">×</span>
        </header>
        <div className="wl-preview-modal-body">
          <label>{t('designSystem.name')}</label>
          <Input dir="auto" placeholder={t('designSystem.sampleUserName')} />
          <label>{t('designSystem.email')}</label>
          <Input dir="auto" placeholder="alex@example.com" />
        </div>
        <footer className="wl-preview-modal-footer">
          <Button>{t('common.cancel')}</Button>
          <Button
            type="primary"
            style={{ background: merged.colors.gradientPrimary, borderColor: merged.colors.primary }}
          >
            {t('common.save')}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/* ─── Tabbed renderer used by both rail + floating window ─────────────── */

interface PreviewTabsProps {
  merged: BrandConfig;
  active: PreviewSurface;
  onChange: (s: PreviewSurface) => void;
  /** Compact mode reduces typography for the floating PIP. */
  compact?: boolean;
}

/** The order surfaces appear in, everywhere. Roughly the order a person meets
 *  them: sign in, land, talk, then the places they manage things. */
export const PREVIEW_SURFACE_ORDER: PreviewSurface[] = [
  'login', 'home', 'chat', 'conversation', 'agents', 'library', 'editAgent',
  'sidebar', 'modal',
];

/**
 * The single mapping from a surface to the thing that draws it.
 *
 * Six of these are the REAL pages (see RealPagePreviews). `sidebar` and
 * `modal` are still canonical mini-renderings: the sidebar needs an auth'd
 * nav state and the modal preview is deliberately generic chrome rather than
 * any one dialog.
 */
export function renderSurface(surface: PreviewSurface, merged: BrandConfig) {
  switch (surface) {
    case 'login':        return <LoginPreview merged={merged} />;
    case 'home':         return <HomePreview merged={merged} />;
    case 'chat':         return <ChatPreview />;
    case 'conversation': return <ConversationPreview />;
    case 'agents':       return <AgentsPreview />;
    case 'library':      return <LibraryPreview merged={merged} />;
    case 'editAgent':    return <EditAgentPreview />;
    case 'sidebar':      return <SidebarPreview merged={merged} />;
    case 'modal':        return <ModalPreview merged={merged} />;
  }
}

/**
 * The surface switcher.
 *
 * A WRAPPING button group rather than an Ant Design <Tabs>. With nine
 * surfaces, a tab strip in this narrow rail overflows and pushes the last few
 * into a "..." dropdown — so the surfaces most worth checking after a brand
 * change would be the ones you cannot see. Buttons that wrap keep all nine on
 * screen and cost one row of height.
 *
 * Only the ACTIVE surface renders. Mounting nine real pages at once would put
 * nine scaled viewports and a live BotModal in the tree for no benefit.
 */
export function PreviewTabs({ merged, active, onChange, compact = false }: PreviewTabsProps) {
  const { t } = useLanguage();
  const surfaceLabel = t('superAdmin.wl.previewSurfaceLabel');
  return (
    <div className={`wl-surface-switch ${compact ? 'wl-surface-switch--compact' : ''}`}>
      <div className="wl-surface-switch-bar" role="tablist" aria-label={surfaceLabel}>
        {PREVIEW_SURFACE_ORDER.map(key => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            className={`wl-surface-btn ${active === key ? 'wl-surface-btn--active' : ''}`}
            onClick={() => onChange(key)}
            data-testid={`wl-surface-${key}`}
          >
            {t(PREVIEW_SURFACE_LABEL_KEY[key])}
          </button>
        ))}
      </div>
      <div className="wl-surface-body" data-surface={active}>
        {renderSurface(active, merged)}
      </div>
    </div>
  );
}

/**
 * Stack ALL surfaces vertically — the "show me every page at once" mode.
 * Triggered by the `gridMode` toggle on the preview header so designers can
 * spot-check the whole change without flipping tabs.
 */
export function PreviewStack({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  return (
    <div className="wl-preview-stack">
      {PREVIEW_SURFACE_ORDER.map(key => (
        <section key={key} className="wl-preview-stack-section" data-surface={key}>
          <header className="wl-preview-stack-header">{t(PREVIEW_SURFACE_LABEL_KEY[key])}</header>
          <div className="wl-preview-stack-body">{renderSurface(key, merged)}</div>
        </section>
      ))}
    </div>
  );
}
